import { useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, Input } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getGiftCard, issueGiftCard } from "../../api/giftCardsApi";
import type { GiftCardOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { canWritePromotions } from "../../roles/permissions";

/**
 * Gift cards — `POST /gift-cards` (issue), `GET /gift-cards/{code}` (lookup balance)
 * (§15.3). There is no `GET /gift-cards` list route on the backend — gift cards are
 * looked up individually by code, matching how a till would actually use one (a customer
 * presents a code, you look it up), so this page is issue + single-code lookup, not a
 * browsable catalog. Redemption happens at sale time via `POST /sales`, not here.
 */
export function GiftCardsPage() {
  const { user, ensureFreshToken } = useAuth();
  const canIssue = user ? canWritePromotions(user.role) : false;

  const [issueCode, setIssueCode] = useState("");
  const [initialBalance, setInitialBalance] = useState("");
  const [branchId, setBranchId] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issued, setIssued] = useState<GiftCardOut | null>(null);

  const [lookupCode, setLookupCode] = useState("");
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookedUp, setLookedUp] = useState<GiftCardOut | null>(null);

  async function handleIssue(event: FormEvent) {
    event.preventDefault();
    setIssueError(null);
    setIssued(null);
    setIssuing(true);
    try {
      const token = await ensureFreshToken();
      const card = await issueGiftCard(token, {
        code: issueCode || undefined,
        initial_balance: asDecimalString(initialBalance),
        branch_id: branchId || undefined,
      });
      setIssued(card);
      setIssueCode("");
      setInitialBalance("");
    } catch (error) {
      setIssueError(error instanceof ApiError ? error.message : "Could not issue gift card — the code may already exist.");
    } finally {
      setIssuing(false);
    }
  }

  async function handleLookup(event: FormEvent) {
    event.preventDefault();
    setLookupError(null);
    setLookedUp(null);
    setLooking(true);
    try {
      const token = await ensureFreshToken();
      const card = await getGiftCard(token, lookupCode.trim());
      setLookedUp(card);
    } catch (error) {
      setLookupError(error instanceof ApiError ? error.message : "Gift card not found.");
    } finally {
      setLooking(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Gift Cards</h1>
          <p className="page__subtitle">Issue store-credit gift cards and look up a balance by code. Redemption happens at sale time.</p>
        </div>
      </div>

      {canIssue ? (
        <div className="panel">
          <h2 className="panel__title">Issue gift card</h2>
          <form onSubmit={handleIssue}>
            <div className="form-grid">
              <Input label="Code (blank = auto-generate)" value={issueCode} onChange={(e) => setIssueCode(e.target.value)} placeholder="GC-XXXXXXXXXXXX" />
              <Input label="Initial balance" inputMode="decimal" value={initialBalance} onChange={(e) => setInitialBalance(e.target.value)} required />
              <BranchSelect id="giftcard-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="Tenant-wide" />
            </div>
            {issueError ? <StatusBanner kind="error">{issueError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={issuing}>
                {issuing ? "Issuing…" : "Issue gift card"}
              </Button>
            </div>
          </form>
          {issued ? (
            <div className="banner banner--info" style={{ marginTop: 16 }}>
              Issued <code>{issued.code}</code> with balance {issued.current_balance}. Hand this code to the customer.
            </div>
          ) : null}
        </div>
      ) : (
        <StatusBanner kind="info">Your role ({user?.role}) can look up gift card balances. Issuing is restricted to OWNER/BRANCH_MANAGER.</StatusBanner>
      )}

      <div className="panel">
        <h2 className="panel__title">Look up balance</h2>
        <form onSubmit={handleLookup}>
          <div className="form-grid">
            <Input label="Code" value={lookupCode} onChange={(e) => setLookupCode(e.target.value)} placeholder="GC-XXXXXXXXXXXX" required />
          </div>
          {lookupError ? <StatusBanner kind="error">{lookupError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" variant="secondary" disabled={looking}>
              {looking ? "Looking up…" : "Look up"}
            </Button>
          </div>
        </form>
        {lookedUp ? (
          <dl style={{ marginTop: 16 }}>
            <div>
              <strong>Code:</strong> <code>{lookedUp.code}</code>
            </div>
            <div>
              <strong>Current balance:</strong> {lookedUp.current_balance}
            </div>
            <div>
              <strong>Initial balance:</strong> {lookedUp.initial_balance}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              <span className={`badge ${lookedUp.is_active ? "badge--current" : "badge--overdue"}`}>{lookedUp.is_active ? "Active" : "Inactive"}</span>
            </div>
          </dl>
        ) : null}
      </div>
    </div>
  );
}
